import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../lib/api";
import * as pdfjsLib from "pdfjs-dist";
import { X, ChevronLeft, ChevronRight, Loader2, FileText, File as FileIcon, ZoomIn, ZoomOut } from "lucide-react";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const isImg = (ct) => (ct || "").startsWith("image/");
const isPdf = (ct) => (ct || "") === "application/pdf";

export default function AttachmentPreviewModal({ files, index, onIndex, pid, onClose }) {
  const file = files[index];
  const [imgUrl, setImgUrl] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfPageImg, setPdfPageImg] = useState(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfTotal, setPdfTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const ct = file?.content_type || "";
  const image = isImg(ct);
  const pdf = isPdf(ct);

  // Load content for the current file
  useEffect(() => {
    setScale(1); setOffset({ x: 0, y: 0 }); setImgUrl(null);
    setPdfDoc(null); setPdfPageImg(null); setPdfPage(1); setPdfTotal(0); setError(false);
    if (!file || (!image && !pdf)) return;
    let active = true;
    let obj = null;
    setLoading(true);
    (async () => {
      try {
        if (image) {
          // Immagine: caricamento diretto dallo storage (URL firmato), nessun byte dal backend.
          const { data } = await api.get(`/studio/pratiche/${pid}/allegati-files/${file.id}/content-url`);
          if (!active) return;
          if (data?.url) { setImgUrl(data.url); setLoading(false); return; }
          const { data: blob } = await api.get(`/studio/pratiche/${pid}/allegati-files/${file.id}/content`, { responseType: "blob" });
          if (!active) return;
          obj = URL.createObjectURL(blob); setImgUrl(obj); setLoading(false);
        } else {
          // PDF: prova prima l'URL firmato (caricamento diretto dallo storage), poi fallback
          // allo streaming server-side (il backend invia il file a blocchi, senza bufferizzarlo).
          let doc = null;
          try {
            const { data } = await api.get(`/studio/pratiche/${pid}/allegati-files/${file.id}/content-url`);
            if (data?.url) doc = await pdfjsLib.getDocument({ url: data.url }).promise;
          } catch { doc = null; }
          if (!active) return;
          if (!doc) {
            const { data: blob } = await api.get(`/studio/pratiche/${pid}/allegati-files/${file.id}/content`, { responseType: "blob" });
            if (!active) return;
            const buf = await blob.arrayBuffer();
            doc = await pdfjsLib.getDocument({ data: buf }).promise;
          }
          if (!active) return;
          setPdfDoc(doc); setPdfTotal(doc.numPages); setPdfPage(1);
        }
      } catch { if (active) { setError(true); setLoading(false); } }
    })();
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [file, pid, image, pdf]);

  // Render current PDF page at current zoom
  useEffect(() => {
    if (!pdfDoc) return;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const page = await pdfDoc.getPage(pdfPage);
        const vp = page.getViewport({ scale: 1.5 * scale });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width; canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
        if (active) { setPdfPageImg(canvas.toDataURL("image/jpeg", 0.85)); setLoading(false); }
      } catch { if (active) { setError(true); setLoading(false); } }
    })();
    return () => { active = false; };
  }, [pdfDoc, pdfPage, scale]);

  const go = useCallback((delta) => {
    const n = index + delta;
    if (n >= 0 && n < files.length) onIndex(n);
  }, [index, files.length, onIndex]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [go, onClose]);

  if (!file) return null;

  const zoomBy = (d) => setScale((s) => Math.min(6, Math.max(0.4, +(s + d).toFixed(2))));
  const onWheel = (e) => { if (image || pdf) { e.preventDefault(); zoomBy(e.deltaY < 0 ? 0.2 : -0.2); } };

  const onPointerDown = (e) => {
    if (!image || scale <= 1) return;
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setOffset({ x: drag.current.ox + (e.clientX - drag.current.x), y: drag.current.oy + (e.clientY - drag.current.y) });
  };
  const onPointerUp = () => { drag.current = null; };

  const content = (
    <div className="fixed inset-0 z-[9999] flex flex-col bg-black/90 pointer-events-auto" data-testid="attachment-preview-modal">
      <div className="flex items-center justify-between px-4 py-3 text-white shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate text-white" data-testid="attachment-preview-name">{file.nome}</div>
          <div className="text-[11px] text-slate-300 font-mono">{fmtBytes(file.size)} · {index + 1}/{files.length}</div>
        </div>
        <div className="flex items-center gap-2">
          {(image || pdf) && (
            <>
              <button type="button" onClick={() => zoomBy(-0.2)} data-testid="attachment-zoom-out" className="p-2 rounded text-white hover:bg-white/10"><ZoomOut className="h-5 w-5" /></button>
              <span className="text-xs font-mono w-12 text-center text-white">{Math.round(scale * 100)}%</span>
              <button type="button" onClick={() => zoomBy(0.2)} data-testid="attachment-zoom-in" className="p-2 rounded text-white hover:bg-white/10"><ZoomIn className="h-5 w-5" /></button>
            </>
          )}
          <button type="button" onClick={onClose} data-testid="attachment-preview-close" title="Chiudi (Esc)" aria-label="Chiudi"
            className="flex items-center justify-center h-10 w-10 rounded-full bg-white/15 text-white ring-1 ring-white/40 hover:bg-white/30 transition-colors"><X className="h-6 w-6" /></button>
        </div>
      </div>

      <button type="button" onClick={() => go(-1)} disabled={index === 0} data-testid="attachment-prev"
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/40 text-white hover:bg-black/70 disabled:opacity-30">
        <ChevronLeft className="h-8 w-8" />
      </button>
      <button type="button" onClick={() => go(1)} disabled={index === files.length - 1} data-testid="attachment-next"
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/40 text-white hover:bg-black/70 disabled:opacity-30">
        <ChevronRight className="h-8 w-8" />
      </button>

      <div className="flex-1 overflow-hidden flex items-center justify-center p-4 relative" onWheel={onWheel} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ touchAction: "none" }} data-testid="attachment-preview-backdrop">
        {loading ? (
          <div className="text-slate-300 flex items-center gap-2"><Loader2 className="h-6 w-6 animate-spin" /> Caricamento anteprima…</div>
        ) : error ? (
          <div className="text-slate-400">Impossibile caricare l'anteprima.</div>
        ) : image && imgUrl ? (
          <img src={imgUrl} alt={file.nome} draggable={false}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, cursor: scale > 1 ? "grab" : "default" }}
            className="max-w-full max-h-full object-contain origin-center select-none" data-testid="attachment-preview-image" />
        ) : pdf ? (
          <div className="flex flex-col items-center gap-3 max-h-full" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            {pdfPageImg && <img src={pdfPageImg} alt={`Pagina ${pdfPage}`} className="max-w-full max-h-[74vh] object-contain shadow-2xl rounded bg-white" data-testid="attachment-preview-pdf" />}
            <div className="flex items-center gap-3 text-slate-200 text-sm bg-black/60 rounded-full px-4 py-1.5">
              <button type="button" onClick={() => setPdfPage((p) => Math.max(1, p - 1))} disabled={pdfPage <= 1} data-testid="pdf-prev-page" className="disabled:opacity-30"><ChevronLeft className="h-5 w-5" /></button>
              <span className="font-mono">Pagina {pdfPage} / {pdfTotal}</span>
              <button type="button" onClick={() => setPdfPage((p) => Math.min(pdfTotal, p + 1))} disabled={pdfPage >= pdfTotal} data-testid="pdf-next-page" className="disabled:opacity-30"><ChevronRight className="h-5 w-5" /></button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-slate-300" data-testid="attachment-preview-generic">
            {isPdf(ct) ? <FileText className="h-24 w-24 text-slate-500" /> : <FileIcon className="h-24 w-24 text-slate-500" />}
            <div className="text-lg font-medium text-center px-4">{file.nome}</div>
            <div className="text-sm text-slate-500 font-mono">{fmtBytes(file.size)}</div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
